import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import Select from 'react-select'
import Search from 'pages/Parts/Search'
import Pagination from 'pages/Parts/Pagination'
import DeleteModal from 'pages/Parts/DeleteModal'

import { GET, PGET, DELETE } from 'helpers/api'
import { SET_TABLE_FILTER } from "store/actions"

function Index() {
	const { t, i18n } = useTranslation()
	const dispatch = useDispatch()

	const BASE_URL = '/services/admin/api/product-category-pageList'

	const reduxTableFilter = useSelector(state => state.tableFilter)

	const [pageCount, setPageCount] = useState(0)
	const [totalCount, setTotalCount] = useState(0)

	const [searchValue, setSearchValue] = useState('')
	const [pageSize, setPageSize] = useState(20)
	const [onlyUnused, setOnlyUnused] = useState(false)

	const [categoryMap, setCategoryMap] = useState({})
	const [data, setData] = useState([])

	const [modalDelete, setModalDelete] = useState(false)
	const [deleteId, setDeleteId] = useState(null)

	// URL passed to <Search/> — keeps the "only unused" filter in sync with text search
	const URL = onlyUnused ? `${BASE_URL}?unused=true` : BASE_URL

	function localName(item) {
		if (i18n.language === 'ru') return item.nameRu
		if (i18n.language === 'uz-Cyrl-UZ') return item.nameUzCyrl
		return item.nameUzLatn
	}

	function parentName(item) {
		if (!item.parentId) return '-'
		return categoryMap[item.parentId] || ('#' + item.parentId)
	}

	function buildFilter(extra = {}) {
		const filter = { size: pageSize, search: searchValue, ...extra }
		if (onlyUnused) filter.unused = true
		return filter
	}

	async function getCategoryMap() {
		const response = await GET('/services/admin/api/product-category-helper')
		if (Array.isArray(response)) {
			const map = {}
			response.forEach(item => { map[item.id] = item.name })
			setCategoryMap(map)
		}
	}

	async function getData(fromSearch = false) {
		var filter = buildFilter({ page: 0 })
		if (reduxTableFilter.page === 'productCategories' && !fromSearch) {
			filter = { ...reduxTableFilter.filters, size: pageSize }
			if (onlyUnused) filter.unused = true
			if (reduxTableFilter?.filters?.search) {
				setSearchValue(reduxTableFilter?.filters?.search)
			}
		} else if (fromSearch) {
			setSearchValue('')
			filter.search = ''
		}

		const response = await PGET(BASE_URL, filter, { loader: true })
		if (response) {
			setTotalCount(Math.ceil(response.headers['x-total-count']))
			setPageCount(Math.ceil(response.headers['x-total-count'] / pageSize))
			setData(response.data)
		}
	}

	async function paginate(page) {
		const response = await PGET(BASE_URL, buildFilter({ page: page.selected }), { loader: true })
		setData(response.data)
		dispatch(SET_TABLE_FILTER({
			'page': 'productCategories',
			'filters': {
				'page': page.selected,
				'search': searchValue,
				'size': pageSize
			}
		}))
	}

	function search(data, searchValue, total) {
		setData(data)
		setSearchValue(searchValue)
		dispatch(SET_TABLE_FILTER({
			'page': 'productCategories',
			'filters': {
				'page': 0,
				'search': searchValue,
				'size': pageSize
			}
		}))
		setPageCount(Math.ceil(total / pageSize))
	}

	function openDeleteModal(id) {
		setDeleteId(id)
		setModalDelete(true)
	}

	async function deleteItem() {
		const response = await DELETE('/services/admin/api/product-category/' + deleteId, { loader: true })
		setModalDelete(false)
		setDeleteId(null)
		// On success the backend returns { success: true }. Validation failures (not public,
		// used in products, has sub-categories) return 400 and are toasted by the api helper.
		if (response && response.success) {
			getData()
			getCategoryMap()
		}
	}

	function handlePageSizeChange(selectedOption) {
		setPageSize(selectedOption.value)
	}

	const pageSizeOptions = [
		{ value: 20, label: '20' },
		{ value: 50, label: '50' },
		{ value: 100, label: '100' },
		{ value: 500, label: '500' },
		{ value: 1000, label: '1000' }
	]

	useEffect(() => {
		getCategoryMap()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		getData()
	}, [pageSize, onlyUnused]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between mb-2">
				<h4 className="vertical-center">
					{t('PRODUCT_CATEGORIES')} ({totalCount})
				</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex align-items-center mb-3">
						<div className="me-2">
							<Search url={URL} search={search} getData={getData} searchValue={searchValue}></Search>
						</div>
						<div className="me-3" style={{ minWidth: '120px' }}>
							<Select
								options={pageSizeOptions}
								value={pageSizeOptions.find(option => option.value === pageSize)}
								onChange={handlePageSizeChange}
								placeholder="Page Size"
								isSearchable={false}
							/>
						</div>
						<div className="form-check">
							<input
								id="onlyUnused"
								type="checkbox"
								className="form-check-input"
								checked={onlyUnused}
								onChange={(e) => setOnlyUnused(e.target.checked)}
							/>
							<label htmlFor="onlyUnused" className="form-check-label cursor-pointer">
								{t('SHOW_ONLY_UNUSED')}
							</label>
						</div>
					</div>

					<div className="table-responsive">
						<table className="table table-striped mb-0">
							<thead>
								<tr>
									<th>#</th>
									<th>{t('NAME')}</th>
									<th>{t('PARENT')}</th>
									<th className="text-center">{t('STATUS')}</th>
									<th className="text-center">{t('PRODUCTS_COUNT')}</th>
									<th className="text-center">{t('USED')}</th>
									<th className="text-center">{t('ACTION')}</th>
								</tr>
							</thead>
							<tbody>
								{data.map((item, index) => (
									<tr className="tr-middle" key={index}>
										<td>{item.id}</td>
										<td>{localName(item)}</td>
										<td>{parentName(item)}</td>
										<td className="text-center">
											{item.isPublic
												? <span className="badge bg-success">{t('PUBLIC')}</span>
												: <span className="badge bg-secondary">{t('PRIVATE')}</span>
											}
										</td>
										<td className="text-center">{item.productsCount}</td>
										<td className="text-center">
											{item.productsCount > 0
												? <span className="text-danger">{t('USED')}</span>
												: <span className="text-success">{t('NOT_USED')}</span>
											}
										</td>
										<td>
											<div className="d-flex justify-content-center">
												{item.canDelete ?
													<div className="table-action-button table-action-danger-button"
														title={t('DELETE')}
														onClick={() => openDeleteModal(item.id)}>
														<i className="uil-trash-alt"></i>
													</div>
													:
													<div className="table-action-button table-action-disabled-button"
														title={t('CANNOT_DELETE_CATEGORY')}
														style={{ opacity: 0.4, cursor: 'not-allowed' }}>
														<i className="uil-trash-alt"></i>
													</div>
												}
											</div>
										</td>
									</tr>
								))
								}
							</tbody>
						</table>
					</div>

					<Pagination pageCount={pageCount} paginate={paginate} reduxPage={reduxTableFilter?.filters?.page}></Pagination>
				</div>
			</div>

			<DeleteModal modalDelete={modalDelete} setModalDelete={setModalDelete} deleteItem={deleteItem}></DeleteModal>
		</>
	)
}

export default Index
